import React, { useState, useEffect, useContext } from "react";

// ######### MATERIAL UI
import { makeStyles, Select, MenuItem, FormControl } from "@material-ui/core";

// ######### CUSTOM COMPONENTS
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import SettingsIcon from "@material-ui/icons/Settings";

// ######### INTERNAL COMPONENTS
import PanelInputs from "./viewComponents/PanelInputs/PanelInputs";
import DrawerFileIngestion from "./viewComponents/DrawerFileIngestion/DrawerFileIngestion";

import {
  getInputMetadata,
  getInputColumns,
  getInputFiles,
  getIngestionColumns,
} from "./viewServices/apiCalls";

// ######### AUTH
import AuthContext from "connections/auth/AuthProvider.js";
import { useTranslation } from "react-i18next";

// ######### CONSTANTS
import {
  CALCULATION_METHODS,
  CALCULATION_METHOD_OPTIONS,
} from "services/utils/calculationConstants";

// ######### STYLES
const inputsStyle = (theme, clientColor) => ({
  cardHeaderCard: {
    marginTop: theme.spacing(-2),
    marginBottom: theme.spacing(1),
  },
  cardHeaderTitle: {
    ...theme.typography.h6,
    fontSize: "1.125rem",
    color: theme.palette.secondary.main,
    fontWeight: "400",
    marginBottom: "10px",
    marginTop: "10px",
  },
  dropdownContainer: {
    position: "absolute",
    right: "0px",
    top: "50%",
    transform: "translateY(-50%)",
  },
  selectFormControl: {
    marginBottom: 0,
    minWidth: 220,
    margin: "0 !important",
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: "#999 !important",
      },
      "&:after": {
        borderBottomColor: clientColor + " !important",
      },
    },
  },
});

// ######### COMPONENT
export default function InputsView() {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;
  const useStyles = makeStyles((theme) => inputsStyle(theme, clientColor));
  const classes = useStyles();

  const T_PREFIX = "rgm.inputs";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  // ######### GLOBAL STATES
  const [inputMetadata, setInputMetadata] = useState({});
  const [inputColumns, setInputColumns] = useState({});
  const [inputFiles, setInputFiles] = useState({});
  const [ingestionColumns, setIngestionColumns] = useState({});
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(true);
  const [isLoadingColumns, setIsLoadingColumns] = useState(true);

  // ######### CALCULATION METHOD FILTER
  const [selectedCalculationMethod, setSelectedCalculationMethod] = useState(
    CALCULATION_METHODS.ALL,
  );

  // ######### DRAWER CONTROL
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedInputTable, setSelectedInputTable] = useState("");

  const handleSelectTable = (tableId) => {
    setSelectedInputTable(tableId);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedInputTable("");
  };

  const handleMetadataUpdate = (tableId, updatedMetadata) => {
    setInputMetadata((prev) => ({
      ...prev,
      [tableId]: updatedMetadata,
    }));
  };

  // ######### LOAD FUNCTIONS
  const loadMetadata = async () => {
    setIsLoadingMetadata(true);
    try {
      const metadata = await getInputMetadata();
      setInputMetadata(metadata);
    } catch (err) {
      console.error("Failed to fetch input metadata", err);
    } finally {
      setIsLoadingMetadata(false);
    }
  };

  const loadColumnsAndFiles = async () => {
    setIsLoadingColumns(true);
    try {
      const [columns, files, ingestionCols] = await Promise.all([
        getInputColumns(),
        getInputFiles(),
        getIngestionColumns(),
      ]);
      setInputColumns(columns);
      setInputFiles(files);
      setIngestionColumns(ingestionCols);
    } catch (err) {
      console.error("Failed to fetch input columns or files", err);
    } finally {
      setIsLoadingColumns(false);
    }
  };

  // ######### INITIAL LOAD
  useEffect(() => {
    loadMetadata();
    loadColumnsAndFiles();
  }, []);

  // ######### RETURN VIEW
  return (
    <div>
      <Card className={classes.cardHeaderCard}>
        <CardHeader color="client" icon style={{ position: "relative" }}>
          <CardIcon color="client">
            <SettingsIcon />
          </CardIcon>
          <h4 className={classes.cardHeaderTitle}>{t("title")}</h4>
          <div className={classes.dropdownContainer}>
            <FormControl className={classes.selectFormControl}>
              <Select
                value={selectedCalculationMethod}
                onChange={(e) => setSelectedCalculationMethod(e.target.value)}
                displayEmpty
              >
                {CALCULATION_METHOD_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </CardHeader>
      </Card>
      <PanelInputs
        selectedInputTable={selectedInputTable}
        setSelectedInputTable={handleSelectTable}
        inputMetadata={inputMetadata}
        inputColumns={inputColumns}
        isLoadingMetadata={isLoadingMetadata}
        selectedCalculationMethod={selectedCalculationMethod}
      />

      <DrawerFileIngestion
        open={drawerOpen}
        onClose={handleCloseDrawer}
        selectedInputTable={selectedInputTable}
        inputMetadata={inputMetadata}
        inputColumns={inputColumns}
        inputFiles={inputFiles}
        ingestionColumns={ingestionColumns}
        isLoadingMetadata={isLoadingMetadata}
        isLoadingColumns={isLoadingColumns}
        onMetadataRefresh={loadMetadata}
        onColumnsRefresh={loadColumnsAndFiles}
        onMetadataUpdate={handleMetadataUpdate}
        userDetails={userDetails}
      />
    </div>
  );
}
